import PermissionButton from '../../permissions/PermissionButton';
import React, { useState } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  Card,
  Select,
  Slider,
  message,
  Typography,
  Divider,
} from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  ScheduleOutlined,
} from "@ant-design/icons";
import { useCreateClient } from "../../hooks/clientHooks";
import CommercialSelect from "../com/CommercialSelect";
import { useCities, usePaymentModes } from '../../hooks/listsHooks';

const { Option } = Select;
const { Title, Paragraph } = Typography;

const ClientForm = ({ onSuccess }) => {
  
  const { handleCreate } = useCreateClient();
  const [form] = Form.useForm();
  const { cities } = useCities();
  const { modes: paymentModes } = usePaymentModes();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await handleCreate({
        ...values,
        echeance: parseInt(values.echeance, 10) || 0,
        REMISE_G: parseFloat(values.REMISE_G) || 0.0,
        plafond: parseFloat(values.plafond) || 0.0,
        code_com: values.code_com ? parseInt(values.code_com, 10) : null,
        SOLDE: parseInt(values.SOLDE, 10) || 0,
        cond_paie: values.cond_paie ? parseInt(values.cond_paie, 10) : null,
        code_cat: values.code_cat ? parseInt(values.code_cat, 10) : null,
      });
      form.resetFields();
      message.success("Client créé avec succès");
      onSuccess?.();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "La création du client a échoué");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: "#ececec", padding: "20px" }}>
      <Card
        style={{
          marginTop: "20px",
          backgroundColor: "#f8f8f8",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Title
          level={2}
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            color: "#333",
            marginBottom: "16px",
            borderBottom: "2px solid #ccc",
            paddingBottom: "10px",
            marginTop: "10px",
          }}
        >
          Créer un client
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Renseignez les informations clés pour simplifier la création des devis, commandes et règlements.
        </Paragraph>
        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
        <Divider orientation="left">Informations générales</Divider>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    <UserOutlined /> Nom du client
                  </span>
                }
                name="nom"
                rules={[{ required: true }]}
                style={{ marginBottom: "16px" }}
              >
                <Input placeholder="Raison sociale" />
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    <PhoneOutlined /> Téléphone
                  </span>
                }
                name="tel"
                style={{ marginBottom: "16px" }}
              >
                <Input placeholder="Coordonnées téléphoniques" />
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    <EnvironmentOutlined /> Adresse
                  </span>
                }
                name="adresse"
                style={{ marginBottom: "16px" }}
              >
                <Input placeholder="Adresse de facturation ou livraison" />
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    <EnvironmentOutlined /> Ville
                  </span>
                }
                name="ville"
                style={{ marginBottom: "16px" }}
              >
                <Select placeholder="Choisir une ville">
                  {cities.map((city) => (
                    <Option key={city} value={city}>
                      {city}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    <EnvironmentOutlined /> Notes
                  </span>
                }
                name="note"
                style={{ marginBottom: "16px" }}
              >
                <Input.TextArea rows={3} placeholder="Informations internes (facultatif)" />
              </Form.Item>
              <Form.Item
                name="code_com"
                label={<span><UserOutlined /> Commercial</span>}
              >
                <CommercialSelect />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Divider orientation="left">Conditions commerciales</Divider>
              <Form.Item
                label={
                  <span>
                    <ScheduleOutlined /> Échéance (jours)
                  </span>
                }
                name="echeance"
                style={{ marginBottom: "16px" }}
              >
                <Input placeholder="Nombre de jours" type="number" />
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    <EnvironmentOutlined /> Mode de paiement
                  </span>
                }
                name="mode_paie"
                style={{ marginBottom: "16px" }}
              >
                <Select placeholder="Choisir un mode de règlement">
                  {paymentModes.map((mode) => (
                    <Option key={mode} value={mode}>
                      {mode}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    <DollarOutlined /> Remise globale (%)
                  </span>
                }
                name="REMISE_G"
                style={{ marginBottom: "16px" }}
              >
                <Slider
                  min={0}
                  max={100}
                  marks={{ 0: "0%", 50: "50%", 100: "100%" }}
                />
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    <DollarOutlined /> Plafond autorisé
                  </span>
                }
                name="plafond"
                style={{ marginBottom: "16px" }}
              >
                <Input placeholder="Montant maximum autorisé" type="number" />
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    <DollarOutlined /> Solde actuel
                  </span>
                }
                name="SOLDE"
                style={{ marginBottom: "16px" }}
              >
                <Input placeholder="Solde client" type="number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <PermissionButton permissionModule="clients" permissionAction="create" type="primary" htmlType="submit" style={{ width: "100%" }} loading={submitting}>
              Enregistrer
            </PermissionButton>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ClientForm;
